import {
    type Action,
    type ActionExample,
    type IAgentRuntime,
    type Memory,
    type State,
    type HandlerCallback,
    elizaLogger,
    composePromptFromState,
    parseKeyValueXml,
    ModelType,
    type Content,
} from "@elizaos/core";
import { approve, getTxReceipt, swap, getQuote } from "../utils";
import type { Address } from "viem";
import { validateAvalancheConfig } from "../environment";
import { TOKEN_ADDRESSES, YAK_SWAP_CONFIG } from "../utils/constants";

export interface SwapContent extends Content {
    fromTokenAddress: string;
    toTokenAddress: string;
    recipient?: string;
    amount: string | number;
}

// refactoring zone
function isSwapContent(
    _runtime: IAgentRuntime,
    content: unknown
): content is SwapContent {
    elizaLogger.debug("Content for swap", content);
    return (
        typeof content === "object" &&
        content !== null &&
        "fromTokenAddress" in content &&
        "toTokenAddress" in content &&
        typeof (content as SwapContent).fromTokenAddress === "string" &&
        typeof (content as SwapContent).toTokenAddress === "string" &&
        (typeof (content as SwapContent).recipient === "string" || !(content as SwapContent).recipient) &&
        (typeof (content as SwapContent).amount === "string" ||
            typeof (content as SwapContent).amount === "number")
    );
}

const transferTemplate = `Respond with an XML block containing only the extracted values. Use key-value pairs.
- Use null for any values that cannot be determined.
- Use address zero for native AVAX transfers.
- If our balance is not enough, use null for the amount.

Example response for a 10 AVAX to USDC swap:
<response>
    <fromTokenAddress>0x0000000000000000000000000000000000000000</fromTokenAddress>
    <toTokenAddress>0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E</toTokenAddress>
    <recipient>null</recipient>
    <amount>10</amount>
</response>

Example response for a 10 WAVAX to USDC swap:
<response>
    <fromTokenAddress>0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7</fromTokenAddress>
    <toTokenAddress>0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E</toTokenAddress>
    <recipient>0xDcEDF06Fd33E1D7b6eb4b309f779a0e9D3172e44</recipient>
    <amount>10</amount>
</response>

Example response to buy WAVAX with 5 USDC:
<response>
    <fromTokenAddress>0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E</fromTokenAddress>
    <toTokenAddress>0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7</toTokenAddress>
    <recipient>0xDcEDF06Fd33E1D7b6eb4b309f779a0e9D3172e44</recipient>
    <amount>5</amount>
</response>

Example response to sell 5 USDC for gmYAK:
<response>
    <fromTokenAddress>0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E</fromTokenAddress>
    <toTokenAddress>0x3A30784c1af928CdFce678eE49370220aA716DC3</toTokenAddress>
    <recipient>0xDcEDF06Fd33E1D7b6eb4b309f779a0e9D3172e44</recipient>
    <amount>5</amount>
</response>

## Token Addresses

${Object.entries(TOKEN_ADDRESSES)
    .map(([key, value]) => `- ${key}: ${value}`)
    .join("\n")}

## Recent Messages

{{recentMessages}}

Given the recent messages, extract the following information about the requested token transfer:
- From token address (the token to sell)
- To token address (the token to buy)
- Recipient wallet address (optional)
- Amount to sell

Respond with an XML block containing only the extracted values.`;

export default {
    name: "SWAP_TOKEN",
    similes: ["TRADE_TOKEN", "BUY_TOKEN", "SELL_TOKEN"],
    validate: async (runtime: IAgentRuntime, _message: Memory) => {
        await validateAvalancheConfig(runtime);
        return true;
    },
    description:
        "MUST use this action if the user requests swap a token, the request might be varied, but it will always be a token swap.",
    handler: async (
        runtime: IAgentRuntime,
        message: Memory,
        state: State,
        _options: { [key: string]: unknown },
        callback?: HandlerCallback
    ) => {
        elizaLogger.log("Starting SWAP_TOKEN handler...");

        // Initialize or update state
        let currentState = state;
        if (!currentState) {
            currentState = await runtime.composeState(message);
        } else {
            currentState = await runtime.composeState(message, [
                "RECENT_MESSAGES",
            ]);
        }

        // Compose prompt from state
        const prompt = composePromptFromState({
            state: currentState,
            template: transferTemplate,
        });

        // Generate content using the model
        const result = await runtime.useModel(ModelType.TEXT_SMALL, {
            prompt,
            stopSequences: [],
        });

        // Parse the result
        const content = parseKeyValueXml(result);

        // Validate swap content
        if (!isSwapContent(runtime, content)) {
            elizaLogger.error("Invalid content for SWAP_TOKEN action.");
            callback?.({
                text: "Unable to process swap request. Invalid content provided.",
                content: { error: "Invalid swap content" },
            });
            return false;
        }

        // Log the swap content
        elizaLogger.debug("Swap content:", content);
        const quote = await getQuote(
            runtime,
            content.fromTokenAddress as Address,
            content.toTokenAddress as Address,
            content.amount as number
        );
        // return

        if (
            content.fromTokenAddress ===
            "0x0000000000000000000000000000000000000000"
        ) {
            // todo: swap from native
            elizaLogger.log("Swapping from native AVAX");
        } else if (
            content.toTokenAddress ===
            "0x0000000000000000000000000000000000000000"
        ) {
            // todo: swap to native
            elizaLogger.log("Swapping to native AVAX");
        } else {
            const yakRouterAddress = YAK_SWAP_CONFIG.router as Address;
            const tx = await approve(
                runtime,
                content.fromTokenAddress as Address,
                yakRouterAddress,
                content.amount as number
            );
            callback?.({
                text: "approving token...",
                content: { success: true },
            });

            if (tx) {
                let receipt = await getTxReceipt(runtime, tx);

                if (receipt.status === "success") {
                    callback?.({
                        text: "token approved, swapping...",
                        content: { success: true, txHash: tx },
                    });
                    const swapTx = await swap(runtime, quote);
                    if (swapTx) {
                        receipt = await getTxReceipt(runtime, swapTx);
                        if (receipt.status === "success") {
                            elizaLogger.log("Swap successful");
                            callback?.({
                                text: "swap successful",
                                content: { success: true, txHash: swapTx },
                            });
                        } else {
                            elizaLogger.error("Swap failed");
                            callback?.({
                                text: "swap failed",
                                content: { error: "Swap failed" },
                            });
                        }
                    }
                } else {
                    elizaLogger.error("Approve failed");
                    callback?.({
                        text: "approve failed",
                        content: { error: "Approve failed" },
                    });
                }
            } else {
                callback?.({
                    text: "approve failed",
                    content: { error: "Approve failed" },
                });
            }
        }

        return true;
    },
    examples: [
        [
            {
                user: "{{user1}}",
                content: { text: "Swap 1 AVAX for USDC" },
            },
        ],
        [
            {
                user: "{{user1}}",
                content: { text: "Swap 10 USDC for gmYAK" },
            },
        ],
    ] as ActionExample[][],
} as Action;
